import {
  Button,
  getUserIdFromRefreshToken,
  Icon_Kebab,
  Menu,
  useManagedModal,
} from "@shapr3d/frontend-lib";

import { type Comment, useMutateDeleteComment } from "../../hooks/useComments";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { useWebExport } from "../../hooks/useWebExport";
import { Modal } from "../../lib/modal";

import style from "./CommentContextMenu.module.css";

interface CommentContextMenuProps {
  comment: Comment;
}

export const CommentContextMenu = ({ comment }: CommentContextMenuProps) => {
  const tracker = useTracker();
  const { t, m } = useTranslation();
  const deleteCommentModal = useManagedModal();
  const { data: webExportData, viewLinkId } = useWebExport();

  const { mutateAsync: mutateDeleteComment, isPending: isMutateDeletePending } =
    useMutateDeleteComment();

  return (
    <>
      <Menu
        className={style.contextMenu}
        activator={
          <Button
            variant="borderless"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Icon_Kebab />
          </Button>
        }
        onOpen={() => {
          viewLinkId &&
            tracker.trackCommentContextMenuVisibilityChanged({
              commentId: comment.commentId,
              viewLinkId,
              webExportId: webExportData?.webExport.webExportId,
              visible: true,
            });
        }}
        onClose={() => {
          viewLinkId &&
            tracker.trackCommentContextMenuVisibilityChanged({
              commentId: comment.commentId,
              viewLinkId,
              webExportId: webExportData?.webExport.webExportId,
              visible: false,
            });
        }}
      >
        <Menu.Item
          closeOnClick
          disabled={
            isMutateDeletePending || comment.createdByUserId !== getUserIdFromRefreshToken()
          }
          onClick={async (event) => {
            event.stopPropagation();
            viewLinkId &&
              tracker.trackCommentDeleteIntended({
                commentId: comment.commentId,
                viewLinkId,
                webExportId: webExportData?.webExport.webExportId,
              });
            deleteCommentModal.open();
          }}
        >
          <span className={style.red}>{t(m.Comments.DeleteMenuItem)}</span>
        </Menu.Item>
      </Menu>
      <Modal
        size="small"
        title={t(m.Comments.DeleteModalTitle)}
        {...deleteCommentModal.props}
        telemetryName="delete_comment"
      >
        <div className={style.deleteConfirmationContainer}>
          <span>{t(m.Comments.DeleteModalBody)}</span>
          <Button
            variant={"red"}
            disabled={isMutateDeletePending}
            onClick={async () => {
              try {
                await mutateDeleteComment({ commentId: comment.commentId });
              } finally {
                viewLinkId &&
                  tracker.trackCommentDeleteConfirmed({
                    commentId: comment.commentId,
                    viewLinkId,
                    webExportId: webExportData?.webExport.webExportId,
                  });

                deleteCommentModal.close();
              }
            }}
          >
            {t(m.Comments.DeleteButton)}
          </Button>
        </div>
      </Modal>
    </>
  );
};
